import { useState, useEffect } from "react";
import { t, font } from "@realtyflows/ui";
import { getFolderHealth, getDealHealth } from "../api";

/**
 * HealthScore — V2 Features 2.3 / 2.8
 *
 * Renders a 0–100 folder or deal health score with a factor breakdown.
 * Pulls live from the API; falls back gracefully if not available.
 *
 * Props:
 *   folderId  — show folder-level health (mutually exclusive with dealId)
 *   dealId    — show deal-level health (aggregated across folders)
 *   compact   — small inline badge mode (no factor breakdown)
 */
export default function HealthScore({ folderId, dealId, compact = false }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fn = folderId ? getFolderHealth(folderId) : dealId ? getDealHealth(dealId) : null;
    if (!fn) { setLoading(false); return; }
    fn.then(setHealth).catch(() => setHealth(null)).finally(() => setLoading(false));
  }, [folderId, dealId]);

  if (loading || !health) return null;

  const color = health.tier === "healthy" ? t.emerald : health.tier === "watch" ? t.amber : t.coral;
  const tierLabel = health.tier === "healthy" ? "Healthy" : health.tier === "watch" ? "Watch" : "At risk";

  if (compact) {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "2px 8px", borderRadius: 99,
        background: color + "18", border: `1px solid ${color}40`,
        color, fontFamily: font.mono, fontSize: 11, fontWeight: 700,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: 99, background: color }} />
        {health.score} · {tierLabel}
      </span>
    );
  }

  const fmtCloseDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div>
      {/* Big score */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 36, fontWeight: 800, color, fontFamily: font.mono, lineHeight: 1 }}>
          {health.score}
        </span>
        <span style={{ fontSize: 12, color: t.textMuted, fontFamily: font.mono }}>/ 100</span>
        <span style={{
          marginLeft: 6, padding: "3px 9px", borderRadius: 99,
          background: color + "18", color, fontFamily: font.mono, fontSize: 11, fontWeight: 700,
        }}>
          {tierLabel}
        </span>
        {health.stalled && (
          <span style={{
            marginLeft: 4, padding: "3px 9px", borderRadius: 99,
            background: t.coral + "18", color: t.coral, fontFamily: font.mono, fontSize: 11, fontWeight: 700,
          }}>
            ⚠ Stalled
          </span>
        )}
      </div>

      {/* Factor breakdown */}
      {health.factors && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
          <Factor label="Compliance" value={health.factors.compliance} />
          <Factor label="Signing" value={health.factors.signing} />
          <Factor label="Activity" value={health.factors.activity} />
          <Factor label="Stage age" value={health.factors.age} />
        </div>
      )}

      {/* Predicted close date (V2 Feature 2.9) */}
      {health.predictedCloseDate && (
        <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
          Predicted close: <strong style={{ color: t.textPrimary }}>{fmtCloseDate(health.predictedCloseDate)}</strong>
        </div>
      )}
      {health.daysSinceActivity != null && (
        <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
          Last activity: {health.daysSinceActivity === 0 ? "today" : `${health.daysSinceActivity}d ago`}
        </div>
      )}
    </div>
  );
}

function Factor({ label, value }) {
  const color = value >= 75 ? t.emerald : value >= 50 ? t.amber : t.coral;
  return (
    <div style={{ background: t.surfaceWarm, borderRadius: 6, padding: "8px 10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, fontFamily: font.body, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {label}
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color, fontFamily: font.mono }}>
          {Math.round(value)}
        </span>
      </div>
      <div style={{ height: 3, background: t.border, borderRadius: 99, marginTop: 4, overflow: "hidden" }}>
        <div style={{ width: `${value}%`, height: "100%", background: color, transition: "width 0.3s" }} />
      </div>
    </div>
  );
}
